import React, { useEffect, useRef, useState } from 'react';
import { PhoneCall, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import backendApi from '../../services/backendApi';
import { announceIntercomCall } from '../../utils/pomodoroAnnouncements';
import { isIntercomTvDashboard, subscribeToIntercomTvMode } from '../../utils/intercomDevice';
import './personalIntercomListener.css';

function PersonalIntercomListener() {
  const { user, isInternalUser } = useAuth();
  const [activeCall, setActiveCall] = useState(null);
  const [isTvMode, setIsTvMode] = useState(isIntercomTvDashboard);
  const knownCallIdsRef = useRef(new Set());
  const hideTimerRef = useRef(null);
  const enabled = Boolean(user && isInternalUser);
  const personalEnabled = enabled && !isTvMode;

  useEffect(() => subscribeToIntercomTvMode(setIsTvMode), []);

  useEffect(() => {
    if (!enabled) return undefined;
    const heartbeat = () => backendApi.markIntercomPresence().catch(() => {});
    const heartbeatWhenVisible = () => {
      if (document.visibilityState === 'visible') heartbeat();
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, 10000);
    window.addEventListener('focus', heartbeat);
    window.addEventListener('online', heartbeat);
    document.addEventListener('visibilitychange', heartbeatWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', heartbeat);
      window.removeEventListener('online', heartbeat);
      document.removeEventListener('visibilitychange', heartbeatWhenVisible);
    };
  }, [enabled, isTvMode]);

  useEffect(() => {
    if (!personalEnabled) {
      setActiveCall(null);
      return undefined;
    }
    let mounted = true;

    const poll = async () => {
      try {
        const calls = await backendApi.getIntercomCalls('personal');
        if (!mounted) return;
        calls.forEach(call => {
          if (knownCallIdsRef.current.has(call.id)) return;
          knownCallIdsRef.current.add(call.id);
          setActiveCall(call);
          announceIntercomCall(call);
          window.clearTimeout(hideTimerRef.current);
          hideTimerRef.current = window.setTimeout(() => setActiveCall(null), 12000);
        });
      } catch (error) {
        // L'application reste utilisable pendant une interruption de l'interphone.
      }
    };

    poll();
    const timer = window.setInterval(poll, 2000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [personalEnabled]);

  useEffect(() => () => window.clearTimeout(hideTimerRef.current), []);

  if (!personalEnabled || !activeCall) return null;
  return (
    <aside className="personal-intercom-alert" role="alert" aria-live="assertive">
      <span className="personal-intercom-icon" aria-hidden="true"><PhoneCall /></span>
      <div><span>On vous demande</span><strong>{activeCall.caller_name}</strong></div>
      <button type="button" aria-label="Fermer l’appel" onClick={() => setActiveCall(null)}><X /></button>
    </aside>
  );
}

export default PersonalIntercomListener;
